import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { useToast } from '../../hooks/use-toast';
import api, { formatApiError, to12h, getInitials } from '../../lib/api';
import { startPayment } from '../../lib/payments';
import SwimmerSearch from '../../components/admin/SwimmerSearch';
import {
  ArrowLeft, Loader2, CreditCard, CalendarClock, Activity, Phone, Mail, Droplet, Users as UsersIcon, Plus,
  TrendingUp, Trash2, ClipboardList, Target, Minus, X, Crown, KeyRound, Pencil, Snowflake, Hourglass, RefreshCw, Copy,
} from 'lucide-react';

const statusBadge = (s) => ({ active: 'bg-green-100 text-green-800', frozen: 'bg-blue-100 text-blue-800', pending: 'bg-amber-100 text-amber-800', expired: 'bg-gray-100 text-gray-700', cancelled: 'bg-red-100 text-red-800' }[s] || 'bg-gray-100 text-gray-700');
const RATING_LABELS = { 1: 'Not Started', 2: 'Beginner', 3: 'Developing', 4: 'Proficient', 5: 'Mastered' };
const ratingColor = (r) => (r >= 5 ? 'text-emerald-600' : r >= 4 ? 'text-blue-600' : r >= 3 ? 'text-cyan-600' : r >= 2 ? 'text-amber-600' : 'text-gray-500');

const ACT = {
  self_swimming: { label: 'Self Swimming', color: 'bg-cyan-100 text-cyan-800' },
  coaching_kids: { label: 'Coaching (Kids)', color: 'bg-amber-100 text-amber-800' },
  coaching_adults: { label: 'Coaching (Adults)', color: 'bg-blue-100 text-blue-800' },
  coaching_ladies: { label: 'Coaching (Ladies)', color: 'bg-pink-100 text-pink-800' },
  free: { label: 'Available / Free Slot', color: 'bg-gray-100 text-gray-600' },
};
const fmtT = (t) => { if (!t) return ''; const h = Number(t.split(':')[0]); return `${h % 12 || 12} ${h < 12 ? 'AM' : 'PM'}`; };
const slotLbl = (b) => `${fmtT(b.start_time)}–${fmtT(b.end_time)}`;

const MemberDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [swimmer, setSwimmer] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [stats, setStats] = useState(null);
  const [guardians, setGuardians] = useState([]);
  const [loading, setLoading] = useState(true);

  const [plans, setPlans] = useState([]);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [addonCount, setAddonCount] = useState(0);
  const [extraMembers, setExtraMembers] = useState([]);
  const [enrolling, setEnrolling] = useState(false);

  const [guardianOpen, setGuardianOpen] = useState(false);
  const [guardian, setGuardian] = useState({ name: '', phone: '', relationship: 'parent' });

  // Group attendance, edit-members, portal access
  const [groupTotal, setGroupTotal] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editSelected, setEditSelected] = useState([]);
  const [savingMembers, setSavingMembers] = useState(false);
  const [attnData, setAttnData] = useState(null);
  const [portalPwd, setPortalPwd] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  // Class schedule (batch slots)
  const [scheduleSlots, setScheduleSlots] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [addSlotOpen, setAddSlotOpen] = useState(false);
  const [slotDayType, setSlotDayType] = useState('weekday');
  const [slotWarn, setSlotWarn] = useState(null);
  const [enrollSlotIds, setEnrollSlotIds] = useState([]);

  // Progress & training
  const [progress, setProgress] = useState([]);
  const [progressSummary, setProgressSummary] = useState(null);
  const [skillsData, setSkillsData] = useState({ skills: [], rating_scale: [] });
  const [assessOpen, setAssessOpen] = useState(false);
  const [assessForm, setAssessForm] = useState({ ratings: {}, coach_note: '', next_goals: '' });
  const [savingAssess, setSavingAssess] = useState(false);

  const loadProgress = useCallback(async () => {
    try {
      const [list, sum] = await Promise.all([
        api.get('/progress', { params: { swimmer_id: id } }),
        api.get(`/progress/swimmer/${id}/summary`),
      ]);
      setProgress(list.data);
      setProgressSummary(sum.data);
    } catch { /* ignore */ }
  }, [id]);

  const loadSchedule = useCallback(async () => {
    try { setScheduleSlots((await api.get(`/batches/by-swimmer/${id}`)).data); } catch { /* ignore */ }
  }, [id]);

  const load = useCallback(async () => {
    try {
      const [sw, mem, bk, att, st, gu, sl, allb] = await Promise.all([
        api.get(`/swimmers/${id}`),
        api.get('/memberships', { params: { swimmer_id: id } }),
        api.get('/bookings', { params: { swimmer_id: id } }),
        api.get(`/attendance/swimmer/${id}`),
        api.get(`/attendance/swimmer/${id}/stats`),
        api.get(`/swimmers/${id}/guardians`),
        api.get(`/batches/by-swimmer/${id}`),
        api.get('/batches', { params: { active_only: true, limit: 100 } }),
      ]);
      setSwimmer(sw.data); setMemberships(mem.data); setBookings(bk.data);
      setAttendance(att.data); setStats(st.data); setGuardians(gu.data);
      setScheduleSlots(sl.data); setAllSlots(allb.data);
    } catch (err) {
      toast({ title: 'Failed to load member', description: formatApiError(err), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [id, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadProgress(); }, [loadProgress]);
  useEffect(() => { api.get('/membership-plans', { params: { active_only: true } }).then((r) => setPlans(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get('/progress/skills').then((r) => setSkillsData(r.data)).catch(() => {}); }, []);

  const submitAssessment = async () => {
    const skills = Object.entries(assessForm.ratings)
      .filter(([, v]) => Number(v) > 0)
      .map(([skill, v]) => ({ skill, rating: Number(v) }));
    if (skills.length === 0 && !assessForm.coach_note.trim() && !assessForm.next_goals.trim()) {
      toast({ title: 'Add at least one skill rating or a note', variant: 'destructive' });
      return;
    }
    setSavingAssess(true);
    try {
      await api.post('/progress', { swimmer_id: id, skills, coach_note: assessForm.coach_note || null, next_goals: assessForm.next_goals || null });
      toast({ title: 'Assessment saved 🏊' });
      setAssessOpen(false);
      setAssessForm({ ratings: {}, coach_note: '', next_goals: '' });
      loadProgress();
    } catch (err) {
      toast({ title: 'Failed to save', description: formatApiError(err), variant: 'destructive' });
    } finally { setSavingAssess(false); }
  };

  const deleteProgress = async (recId) => {
    try {
      await api.delete(`/progress/${recId}`);
      toast({ title: 'Record deleted' });
      loadProgress();
    } catch (err) { toast({ title: 'Failed', description: formatApiError(err), variant: 'destructive' }); }
  };

  const basePlan = plans.find((p) => p.id === selectedPlan);
  const isSelfSwimming = basePlan?.category === 'self_swimming';
  const canAddon = isSelfSwimming && ['individual', 'couple'].includes(basePlan?.type);
  const addonPlan = basePlan ? plans.find((p) => p.type === 'add_on' && p.duration_days === basePlan.duration_days) : null;
  const effectiveAddons = canAddon ? addonCount : 0;
  const totalPax = (basePlan?.max_members || 1) + effectiveAddons;
  const additionalSlots = Math.max(0, totalPax - 1);
  const billTotal = (basePlan?.price || 0) + effectiveAddons * (addonPlan?.price || 0);

  const compatibleActivities = isSelfSwimming || basePlan?.category === 'flexible'
    ? ['self_swimming']
    : basePlan?.category === 'training' ? ['coaching_kids', 'coaching_adults', 'coaching_ladies'] : [];
  const compatibleSlots = basePlan ? allSlots.filter((b) => compatibleActivities.includes(b.activity_type)) : [];

  const openEnroll = () => {
    setSelectedPlan(''); setAddonCount(0); setExtraMembers([]); setEnrollSlotIds([]); setEnrollOpen(true);
  };

  const toggleEnrollSlot = (sid) => setEnrollSlotIds((p) => (p.includes(sid) ? p.filter((x) => x !== sid) : [...p, sid]));

  const addExtraMember = (s) => {
    if (s.id === id || extraMembers.some((m) => m.id === s.id)) return;
    if (extraMembers.length >= additionalSlots) {
      toast({ title: 'All member slots filled', description: 'Increase add-ons to cover more people.', variant: 'destructive' });
      return;
    }
    setExtraMembers((p) => [...p, s]);
  };

  const enroll = async () => {
    if (!selectedPlan) { toast({ title: 'Select a plan', variant: 'destructive' }); return; }
    if (extraMembers.length < additionalSlots) {
      toast({ title: `Assign ${additionalSlots} member${additionalSlots !== 1 ? 's' : ''}`, description: 'This package covers more people than selected.', variant: 'destructive' });
      return;
    }
    setEnrolling(true);
    try {
      const pay = await startPayment({
        purpose: 'membership', plan_id: selectedPlan, swimmer_id: id,
        addon_count: effectiveAddons, member_ids: extraMembers.map((m) => m.id),
        prefill: { name: swimmer?.name, contact: swimmer?.phone || '' },
      });
      toast({ title: `Membership activated! 🎉${pay.mock ? ' (Test Mode)' : ''}`, description: `Invoice ${pay.invoice?.invoice_number}` });
      // Enroll into selected class slots (compatible activity, so force past any audience warning)
      for (const sid of enrollSlotIds) {
        try { await api.post(`/batches/${sid}/enroll`, { swimmer_id: id, force: true }); } catch { /* skip */ }
      }
      setEnrollOpen(false); setSelectedPlan(''); setAddonCount(0); setExtraMembers([]); setEnrollSlotIds([]);
      load();
    } catch (err) {
      toast({ title: 'Enrollment failed', description: formatApiError(err), variant: 'destructive' });
    } finally { setEnrolling(false); }
  };

  const membershipAction = async (mid, action, body) => {
    try {
      await api.post(`/memberships/${mid}/${action}`, body || {});
      const pastTense = { freeze: 'frozen', unfreeze: 'unfrozen', renew: 'renewed', cancel: 'cancelled' }[action] || `${action}d`;
      toast({ title: `Membership ${pastTense}` });
      load();
    } catch (err) {
      toast({ title: `${action} failed`, description: formatApiError(err), variant: 'destructive' });
    }
  };

  const addGuardian = async () => {
    if (!guardian.name || !guardian.phone) { toast({ title: 'Name and phone required', variant: 'destructive' }); return; }
    try {
      await api.post(`/swimmers/${id}/guardians`, guardian);
      toast({ title: 'Guardian added' });
      setGuardianOpen(false); setGuardian({ name: '', phone: '', relationship: 'parent' });
      load();
    } catch (err) {
      toast({ title: 'Failed', description: formatApiError(err), variant: 'destructive' });
    }
  };

  // Load combined group attendance for the active membership (powers Total Visits)
  useEffect(() => {
    const active = memberships.find((m) => m.status === 'active');
    if (!active) { setGroupTotal(null); return; }
    api.get(`/memberships/${active.id}/attendance`).then((r) => setGroupTotal(r.data.total_visits)).catch(() => setGroupTotal(null));
  }, [memberships]);

  const openEditMembers = (m) => {
    setEditTarget(m);
    setEditSelected((m.members || []).filter((x) => !x.is_leader).map((x) => ({ id: x.swimmer_id, name: x.name, swimmer_id_code: '' })));
  };

  const addEditMember = (s) => {
    if (!editTarget) return;
    if (s.id === editTarget.swimmer_id || editSelected.some((m) => m.id === s.id)) return;
    if (editSelected.length >= (editTarget.total_pax - 1)) {
      toast({ title: 'All member slots filled', variant: 'destructive' });
      return;
    }
    setEditSelected((p) => [...p, s]);
  };

  const saveEditMembers = async () => {
    setSavingMembers(true);
    try {
      await api.put(`/memberships/${editTarget.id}/members`, { member_ids: editSelected.map((m) => m.id) });
      toast({ title: 'Members updated' });
      setEditTarget(null); setEditSelected([]);
      load();
    } catch (err) {
      toast({ title: 'Update failed', description: formatApiError(err), variant: 'destructive' });
    } finally { setSavingMembers(false); }
  };

  const openAttn = async (m) => {
    try {
      const r = await api.get(`/memberships/${m.id}/attendance`);
      setAttnData({ membership: m, ...r.data });
    } catch (err) { toast({ title: 'Failed', description: formatApiError(err), variant: 'destructive' }); }
  };

  const regeneratePortal = async () => {
    setRegenerating(true);
    try {
      const r = await api.post(`/swimmers/${id}/portal-access`);
      setPortalPwd(r.data.portal_password);
      toast({ title: 'Login password reset', description: 'Share the new password with the client.' });
      setSwimmer((p) => ({ ...p, portal_username: r.data.portal_username, user_id: r.data.user_id }));
    } catch (err) { toast({ title: 'Failed', description: formatApiError(err), variant: 'destructive' }); }
    finally { setRegenerating(false); }
  };

  const enrollSlot = async (batchId, force = false) => {
    try {
      const res = await api.post(`/batches/${batchId}/enroll`, { swimmer_id: id, force });
      toast({ title: res.data.message || 'Added to slot' });
      setSlotWarn(null); setAddSlotOpen(false);
      loadSchedule(); load();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) setSlotWarn({ batchId, message: err.response.data.detail });
      else toast({ title: 'Cannot add to slot', description: formatApiError(err), variant: 'destructive' });
    }
  };

  const removeSlot = async (batchId) => {
    try { await api.post(`/batches/${batchId}/unenroll`, { swimmer_id: id }); toast({ title: 'Removed from slot' }); loadSchedule(); load(); }
    catch (err) { toast({ title: 'Failed', description: formatApiError(err), variant: 'destructive' }); }
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-10 w-10 animate-spin text-blue-500" /></div>;
  if (!swimmer) return <div className="text-center py-32 text-gray-500">Member not found.</div>;

  const activeMembership = memberships.find((m) => m.status === 'active');

  return (
    <div data-testid="member-detail">
      <Button variant="ghost" onClick={() => navigate('/admin-dashboard/members')} className="mb-4" data-testid="back-to-members">
        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Members
      </Button>

      {/* Header */}
      <Card className="shadow-md border-0 bg-white mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 flex items-center justify-center text-white text-xl font-bold">
              {getInitials(swimmer.name)}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">{swimmer.name}</h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap text-sm text-gray-500">
                <span className="font-mono">{swimmer.swimmer_id_code}</span>
                <Badge variant="secondary" className="capitalize">{swimmer.skill_level}</Badge>
                <Badge variant="secondary" className="capitalize">{swimmer.age_group}</Badge>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 flex-wrap">
                {swimmer.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{swimmer.phone}</span>}
                {swimmer.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{swimmer.email}</span>}
                {swimmer.blood_group && <span className="flex items-center gap-1"><Droplet className="h-3.5 w-3.5" />{swimmer.blood_group}</span>}
              </div>
            </div>
            <Button onClick={openEnroll} className="bg-gradient-to-r from-blue-600 to-cyan-600" data-testid="add-membership-btn">
              <CreditCard className="mr-2 h-4 w-4" /> Add Membership
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-gray-500">Active Membership</p><p className="text-lg font-bold text-gray-900 truncate" data-testid="md-active-membership">{activeMembership?.plan_name || 'None'}</p></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-gray-500">{groupTotal != null ? 'Group Visits' : 'Total Visits (30d)'}</p><p className="text-lg font-bold text-gray-900" data-testid="md-total-visits">{groupTotal != null ? groupTotal : (stats?.total_visits ?? 0)}</p></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-gray-500">Unique Days</p><p className="text-lg font-bold text-gray-900">{stats?.unique_days ?? 0}</p></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-gray-500">Avg Session</p><p className="text-lg font-bold text-gray-900">{stats?.avg_duration_minutes ?? 0} min</p></CardContent></Card>
      </div>

      {/* Portal access */}
      <Card className="shadow-sm border-0 bg-white mb-6" data-testid="portal-access-card">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-cyan-50 flex items-center justify-center"><KeyRound className="h-5 w-5 text-cyan-600" /></div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Client Portal Login</p>
              <p className="text-xs text-gray-500">Login ID: <span className="font-mono font-medium text-gray-700" data-testid="portal-username">{swimmer.portal_username || swimmer.swimmer_id_code}</span>{!swimmer.user_id && ' · no login yet'}</p>
              {portalPwd && <p className="text-xs text-emerald-600 mt-1">New password: <span className="font-mono font-semibold">{portalPwd}</span> <button onClick={() => { navigator.clipboard?.writeText(portalPwd); toast({ title: 'Copied' }); }} className="ml-1 underline">copy</button></p>}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={regeneratePortal} disabled={regenerating} data-testid="reset-portal-btn">
            {regenerating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            {swimmer.user_id ? 'Reset Password' : 'Create Login'}
          </Button>
        </CardContent>
      </Card>

      {/* Memberships */}
      <Card className="shadow-md border-0 bg-white mb-6">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><CalendarClock className="h-5 w-5" /> Memberships</CardTitle></CardHeader>
        <CardContent>
          {memberships.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No memberships yet. Add one above.</p>
          ) : (
            <div className="space-y-3" data-testid="md-memberships">
              {memberships.map((m) => (
                <div key={m.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border border-gray-100 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{m.plan_name}</span>
                      <Badge className={statusBadge(m.status)}>
                        {m.status === 'frozen' && <Snowflake className="h-3 w-3 mr-1" />}
                        {m.status === 'pending' && <Hourglass className="h-3 w-3 mr-1" />}
                        {m.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {m.start_date ? `Start ${m.start_date.slice(0, 10)} · ` : ''}
                      {m.status === 'pending'
                        ? 'Queued — activates when current package ends'
                        : <>Ends {m.end_date?.slice(0, 10)}{m.days_remaining != null && ` · ${m.days_remaining} days left${m.status === 'frozen' ? ' (paused)' : ''}`}</>}
                      {m.sessions_remaining != null && ` · ${m.sessions_remaining} sessions left`}
                      {m.hours_remaining != null && ` · ${m.hours_remaining} hrs left`}
                      {m.total_pax > 1 && ` · ${m.total_pax} pax`}
                    </p>
                    {m.total_pax > 1 && m.members?.length > 0 && (
                      <p className="text-xs text-gray-400 mt-0.5">{m.members.map((mm) => mm.name).filter(Boolean).join(', ')}</p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {m.total_pax > 1 && m.status !== 'pending' && (
                      <Button size="sm" variant="outline" onClick={() => openEditMembers(m)} data-testid={`edit-members-${m.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Members</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => openAttn(m)} data-testid={`attn-${m.id}`}><Activity className="h-3.5 w-3.5 mr-1" />Attendance</Button>
                    {m.status === 'active' && <Button size="sm" variant="outline" onClick={() => membershipAction(m.id, 'freeze', { reason: 'Admin freeze' })} data-testid={`freeze-${m.id}`}>Freeze</Button>}
                    {m.status === 'frozen' && <Button size="sm" variant="outline" onClick={() => membershipAction(m.id, 'unfreeze')} data-testid={`unfreeze-${m.id}`}>Unfreeze</Button>}
                    {['active', 'frozen', 'expired'].includes(m.status) && <Button size="sm" variant="outline" onClick={() => membershipAction(m.id, 'renew')} data-testid={`renew-${m.id}`}>Renew</Button>}
                    {['active', 'frozen', 'pending'].includes(m.status) && <Button size="sm" variant="outline" className="text-red-600" onClick={() => membershipAction(m.id, 'cancel')} data-testid={`cancel-${m.id}`}>Cancel</Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Class Schedule (batch slots) */}
      <Card className="shadow-md border-0 bg-white mb-6" data-testid="schedule-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2"><CalendarClock className="h-5 w-5" /> Class Schedule</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setAddSlotOpen(true)} data-testid="add-slot-btn"><Plus className="h-4 w-4 mr-1" /> Add Slot</Button>
        </CardHeader>
        <CardContent>
          {scheduleSlots.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">Not enrolled in any class slot.</p>
          ) : (
            <div className="space-y-2" data-testid="md-schedule-list">
              {scheduleSlots.map((b) => (
                <div key={b.id} className="flex items-center justify-between gap-3 p-3 border border-gray-100 rounded-lg" data-testid={`sched-slot-${b.id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-medium text-gray-800 capitalize">{b.day_type} · {slotLbl(b)}</span>
                    <Badge className={ACT[b.activity_type]?.color}>{ACT[b.activity_type]?.label}</Badge>
                    {b.coach_name && <Badge variant="secondary" className="text-xs">Coach: {b.coach_name}</Badge>}
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => removeSlot(b.id)} data-testid={`remove-slot-${b.id}`}><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="progress">
        <TabsList data-testid="md-tabs">
          <TabsTrigger value="progress" data-testid="md-tab-progress">Progress</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="md-tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="md-tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="guardians" data-testid="md-tab-guardians">Guardians</TabsTrigger>
        </TabsList>

        <TabsContent value="progress">
          <Card className="border-0 shadow-sm" data-testid="md-progress">
            <CardContent className="p-4 space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-cyan-600" data-testid="md-skill-score">{progressSummary?.overall_average ?? 0}<span className="text-base text-gray-400">/5</span></p>
                    <p className="text-xs text-gray-500">Skill Score</p>
                  </div>
                  <div className="text-sm text-gray-500">
                    <p>{progressSummary?.assessments_count ?? 0} assessment{(progressSummary?.assessments_count ?? 0) !== 1 ? 's' : ''}</p>
                    {progressSummary?.last_assessed_date && <p>Last: {progressSummary.last_assessed_date}</p>}
                  </div>
                </div>
                <Button onClick={() => setAssessOpen(true)} className="bg-gradient-to-r from-cyan-600 to-teal-600" data-testid="add-assessment-btn">
                  <ClipboardList className="mr-2 h-4 w-4" /> Add Assessment
                </Button>
              </div>

              {/* Skill summary */}
              {progressSummary?.skills?.length > 0 ? (
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="md-skill-summary">
                  {progressSummary.skills.map((s) => (
                    <div key={s.skill}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-700">{s.skill}</span>
                        <span className={`text-xs font-medium ${ratingColor(s.rating)}`}>{RATING_LABELS[s.rating]}</span>
                      </div>
                      <Progress value={(s.rating / 5) * 100} className="h-2" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No skill assessments yet. Add one to start tracking progress.</p>
              )}

              {progressSummary?.next_goals && (
                <div className="flex items-start gap-2 bg-cyan-50 rounded-lg p-3" data-testid="md-next-goals">
                  <Target className="h-4 w-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                  <div><p className="text-xs font-medium text-cyan-700">Next Goals</p><p className="text-sm text-gray-700">{progressSummary.next_goals}</p></div>
                </div>
              )}

              {/* Timeline */}
              {progress.length > 0 && (
                <div className="pt-2">
                  <p className="text-sm font-semibold text-gray-900 mb-2">History</p>
                  <div className="space-y-3" data-testid="md-progress-timeline">
                    {progress.map((r) => (
                      <div key={r.id} className="border border-gray-100 rounded-lg p-3" data-testid={`progress-record-${r.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-cyan-500" />{r.date}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">by {r.assessed_by_name || 'Coach'}</span>
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => deleteProgress(r.id)} data-testid={`delete-progress-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </div>
                        {r.skills?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {r.skills.map((s, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{s.skill}: <span className={`ml-1 ${ratingColor(s.rating)}`}>{s.rating}/5</span></Badge>
                            ))}
                          </div>
                        )}
                        {r.coach_note && <p className="text-sm text-gray-600">{r.coach_note}</p>}
                        {r.next_goals && <p className="text-xs text-gray-500 mt-1"><span className="font-medium">Goals:</span> {r.next_goals}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bookings">
          <Card className="border-0 shadow-sm"><CardContent className="p-4">
            {bookings.length === 0 ? <p className="text-sm text-gray-500 py-4">No bookings.</p> : (
              <div className="space-y-2">
                {bookings.map((b) => (
                  <div key={b.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                    <span>{b.service_name || b.booking_type} · {b.date} {to12h(b.start_time)}</span>
                    <span className="flex items-center gap-2"><Badge className="bg-gray-100 text-gray-700">{b.status}</Badge>{'\u20B9'}{(b.amount || 0).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="attendance">
          <Card className="border-0 shadow-sm"><CardContent className="p-4">
            {attendance.length === 0 ? <p className="text-sm text-gray-500 py-4">No attendance records.</p> : (
              <div className="space-y-2">
                {attendance.map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                    <span className="flex items-center gap-2"><Activity className="h-3.5 w-3.5 text-cyan-500" />{a.date} · in {a.check_in_time?.slice(11, 16)}{a.check_out_time ? ` · out ${a.check_out_time.slice(11, 16)}` : ' · in pool'}</span>
                    <span className="text-gray-500">{a.duration_minutes != null ? `${a.duration_minutes} min` : '—'} <Badge variant="secondary">{a.method}</Badge></span>
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="guardians">
          <Card className="border-0 shadow-sm"><CardContent className="p-4">
            <div className="flex justify-end mb-3">
              <Button size="sm" variant="outline" onClick={() => setGuardianOpen(true)} data-testid="add-guardian-btn"><Plus className="mr-1 h-3.5 w-3.5" />Add Guardian</Button>
            </div>
            {guardians.length === 0 ? <p className="text-sm text-gray-500 py-4">No guardians recorded.</p> : (
              <div className="space-y-2">
                {guardians.map((g) => (
                  <div key={g.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                    <span className="flex items-center gap-2"><UsersIcon className="h-3.5 w-3.5 text-gray-400" />{g.name} <span className="text-gray-400 capitalize">({g.relationship})</span></span>
                    <span className="text-gray-500">{g.phone}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Enroll dialog */}
      <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
        <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="enroll-dialog">
          <DialogHeader>
            <DialogTitle>Add Membership</DialogTitle>
            <DialogDescription>Pick a package, add extra members if needed, and collect payment.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div className="space-y-2">
              <Label>Package</Label>
              <Select value={selectedPlan} onValueChange={(v) => { setSelectedPlan(v); setAddonCount(0); setExtraMembers([]); }}>
                <SelectTrigger data-testid="enroll-plan-select"><SelectValue placeholder="Select a package" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {['self_swimming', 'flexible', 'training'].map((cat) => {
                    const catPlans = plans.filter((p) => p.category === cat && p.type !== 'add_on');
                    if (catPlans.length === 0) return null;
                    const label = { self_swimming: 'Self Swimming', flexible: 'Flexible Recharge', training: 'Training' }[cat];
                    return (
                      <SelectGroup key={cat}>
                        <SelectLabel>{label}</SelectLabel>
                        {catPlans.map((p) => (
                          <SelectItem key={p.id} value={p.id} data-testid={`enroll-plan-${p.id}`}>
                            {p.name} — {'\u20B9'}{p.price.toLocaleString('en-IN')}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Add-on stepper (self-swimming individual/couple) */}
            {canAddon && addonPlan && (
              <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 p-3" data-testid="enroll-addon-row">
                <div>
                  <p className="text-sm font-medium text-gray-800">Add-on members</p>
                  <p className="text-xs text-gray-500">{'\u20B9'}{addonPlan.price.toLocaleString('en-IN')} per person ({basePlan.duration_days} days)</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button type="button" size="icon" variant="outline" className="h-8 w-8" disabled={addonCount === 0}
                    onClick={() => { setAddonCount((c) => Math.max(0, c - 1)); setExtraMembers((m) => m.slice(0, ((basePlan?.max_members || 1) + Math.max(0, addonCount - 1)) - 1)); }}
                    data-testid="addon-minus"><Minus className="h-4 w-4" /></Button>
                  <span className="w-6 text-center font-semibold" data-testid="addon-count">{addonCount}</span>
                  <Button type="button" size="icon" variant="outline" className="h-8 w-8"
                    onClick={() => setAddonCount((c) => c + 1)} data-testid="addon-plus"><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            )}

            {/* Members covered */}
            {basePlan && (
              <div className="space-y-2" data-testid="enroll-members">
                <Label>Members ({totalPax} pax)</Label>
                <div className="flex items-center gap-2 rounded-lg border border-cyan-100 bg-cyan-50 px-3 py-2">
                  <Crown className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-medium text-gray-800">{swimmer?.name}</span>
                  <Badge variant="secondary" className="text-xs">Pack Leader</Badge>
                </div>
                {extraMembers.map((m) => (
                  <div key={m.id} className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2" data-testid={`extra-member-${m.id}`}>
                    <span className="text-sm text-gray-800">{m.name} <span className="text-xs text-gray-400 font-mono">{m.swimmer_id_code}</span></span>
                    <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-red-500"
                      onClick={() => setExtraMembers((p) => p.filter((x) => x.id !== m.id))} data-testid={`remove-member-${m.id}`}><X className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
                {extraMembers.length < additionalSlots && (
                  <div className="pt-1" data-testid="member-picker">
                    <p className="text-xs text-gray-500 mb-1">Add {additionalSlots - extraMembers.length} more member{additionalSlots - extraMembers.length !== 1 ? 's' : ''} (from existing members)</p>
                    <SwimmerSearch onSelect={addExtraMember} placeholder="Search member to add" />
                  </div>
                )}
              </div>
            )}

            {/* Bill summary */}
            {basePlan && (
              <div className="rounded-lg bg-gray-900 text-white p-4 space-y-1" data-testid="enroll-bill">
                <div className="flex justify-between text-sm text-gray-300"><span>{basePlan.name}</span><span>{'\u20B9'}{basePlan.price.toLocaleString('en-IN')}</span></div>
                {effectiveAddons > 0 && addonPlan && (
                  <div className="flex justify-between text-sm text-gray-300"><span>Add-on × {effectiveAddons}</span><span>{'\u20B9'}{(addonPlan.price * effectiveAddons).toLocaleString('en-IN')}</span></div>
                )}
                <div className="flex justify-between text-lg font-bold pt-1 border-t border-white/10"><span>Total</span><span data-testid="enroll-total">{'\u20B9'}{billTotal.toLocaleString('en-IN')}</span></div>
              </div>
            )}

            {/* Optional class schedule */}
            {basePlan && compatibleSlots.length > 0 && (
              <div className="space-y-2" data-testid="enroll-slots">
                <Label>Class Slots (optional)</Label>
                <p className="text-xs text-gray-500">{basePlan.category === 'training' ? 'Coaching slots' : 'Self-swimming slots'} matching this package.</p>
                <div className="max-h-40 overflow-auto space-y-1 border border-gray-100 rounded-lg p-2">
                  {compatibleSlots.map((b) => (
                    <label key={b.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer" data-testid={`enroll-slot-opt-${b.id}`}>
                      <input type="checkbox" checked={enrollSlotIds.includes(b.id)} onChange={() => toggleEnrollSlot(b.id)} className="rounded" />
                      <span className="capitalize text-gray-700">{b.day_type} · {slotLbl(b)}</span>
                      <Badge className={`${ACT[b.activity_type]?.color} text-[10px]`}>{ACT[b.activity_type]?.label}</Badge>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollOpen(false)}>Cancel</Button>
            <Button onClick={enroll} disabled={enrolling || !selectedPlan} className="bg-gradient-to-r from-blue-600 to-cyan-600" data-testid="enroll-confirm-btn">
              {enrolling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Enroll & Collect Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guardian dialog */}
      <Dialog open={guardianOpen} onOpenChange={setGuardianOpen}>
        <DialogContent data-testid="guardian-dialog">
          <DialogHeader>
            <DialogTitle>Add Guardian</DialogTitle>
            <DialogDescription>Add an emergency/guardian contact for this member.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="space-y-2"><Label>Name</Label><Input value={guardian.name} onChange={(e) => setGuardian((p) => ({ ...p, name: e.target.value }))} data-testid="guardian-name-input" /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={guardian.phone} onChange={(e) => setGuardian((p) => ({ ...p, phone: e.target.value }))} data-testid="guardian-phone-input" /></div>
            <div className="space-y-2"><Label>Relationship</Label><Input value={guardian.relationship} onChange={(e) => setGuardian((p) => ({ ...p, relationship: e.target.value }))} placeholder="parent / spouse / sibling" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGuardianOpen(false)}>Cancel</Button>
            <Button onClick={addGuardian} data-testid="guardian-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assessment dialog */}
      <Dialog open={assessOpen} onOpenChange={setAssessOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="assessment-dialog">
          <DialogHeader>
            <DialogTitle>New Skill Assessment</DialogTitle>
            <DialogDescription>Rate skills and add coaching notes for {swimmer.name}.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Skill Ratings</Label>
              <div className="space-y-2">
                {skillsData.skills.map((skill) => (
                  <div key={skill} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-gray-700">{skill}</span>
                    <Select
                      value={assessForm.ratings[skill] || '0'}
                      onValueChange={(v) => setAssessForm((p) => ({ ...p, ratings: { ...p.ratings, [skill]: v } }))}
                    >
                      <SelectTrigger className="w-40 h-8" data-testid={`assess-skill-${skill.replace(/\s+/g, '-')}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">— Not rated</SelectItem>
                        {skillsData.rating_scale.map((s) => (<SelectItem key={s.value} value={String(s.value)}>{s.value} · {s.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Coach Note</Label>
              <Textarea value={assessForm.coach_note} onChange={(e) => setAssessForm((p) => ({ ...p, coach_note: e.target.value }))} placeholder="Observations, strengths, areas to improve…" data-testid="assess-note-input" />
            </div>
            <div className="space-y-2">
              <Label>Next Goals</Label>
              <Textarea value={assessForm.next_goals} onChange={(e) => setAssessForm((p) => ({ ...p, next_goals: e.target.value }))} placeholder="What to focus on next session…" data-testid="assess-goals-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssessOpen(false)}>Cancel</Button>
            <Button onClick={submitAssessment} disabled={savingAssess} className="bg-gradient-to-r from-cyan-600 to-teal-600" data-testid="assess-save-btn">
              {savingAssess && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Assessment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit members dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => { if (!o) { setEditTarget(null); setEditSelected([]); } }}>
        <DialogContent data-testid="edit-members-dialog">
          <DialogHeader>
            <DialogTitle>Edit Members</DialogTitle>
            <DialogDescription>{editTarget?.plan_name} · covers {editTarget?.total_pax} pax (incl. pack leader).</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-2">
            <div className="flex items-center gap-2 rounded-lg border border-cyan-100 bg-cyan-50 px-3 py-2">
              <Crown className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium text-gray-800">{swimmer.name}</span>
              <Badge variant="secondary" className="text-xs">Pack Leader</Badge>
            </div>
            {editSelected.map((m) => (
              <div key={m.id} className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2" data-testid={`edit-member-${m.id}`}>
                <span className="text-sm text-gray-800">{m.name}</span>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => setEditSelected((p) => p.filter((x) => x.id !== m.id))} data-testid={`edit-remove-${m.id}`}><X className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
            {editTarget && editSelected.length < (editTarget.total_pax - 1) && (
              <div className="pt-1">
                <p className="text-xs text-gray-500 mb-1">Add {editTarget.total_pax - 1 - editSelected.length} more member(s)</p>
                <SwimmerSearch onSelect={addEditMember} placeholder="Search member to add" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditTarget(null); setEditSelected([]); }}>Cancel</Button>
            <Button onClick={saveEditMembers} disabled={savingMembers} className="bg-gradient-to-r from-blue-600 to-cyan-600" data-testid="save-members-btn">
              {savingMembers && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group attendance dialog */}
      <Dialog open={!!attnData} onOpenChange={(o) => { if (!o) setAttnData(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="attn-dialog">
          <DialogHeader>
            <DialogTitle>Group Attendance</DialogTitle>
            <DialogDescription>{attnData?.membership?.plan_name} · {attnData?.total_visits} total class{attnData?.total_visits !== 1 ? 'es' : ''} attended.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            {(attnData?.members || []).map((mm) => (
              <div key={mm.swimmer_id} className="border border-gray-100 rounded-lg p-3" data-testid={`attn-member-${mm.swimmer_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900 flex items-center gap-1">{mm.is_leader && <Crown className="h-3.5 w-3.5 text-amber-500" />}{mm.name}</span>
                  <Badge variant="secondary" className="text-xs">{mm.classes} class{mm.classes !== 1 ? 'es' : ''}</Badge>
                </div>
                {mm.records.length === 0 ? <p className="text-xs text-gray-400">No classes yet.</p> : (
                  <div className="space-y-1">
                    {mm.records.slice(0, 10).map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-xs text-gray-600">
                        <span>{r.date} · in {r.check_in_time?.slice(11, 16)}{r.check_out_time ? ` · out ${r.check_out_time.slice(11, 16)}` : ''}</span>
                        <Badge variant="outline" className="text-[10px]">{r.method}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add class slot dialog (weekly grid) */}
      <Dialog open={addSlotOpen} onOpenChange={setAddSlotOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-slot-dialog">
          <DialogHeader>
            <DialogTitle>Add to Class Slot</DialogTitle>
            <DialogDescription>Pick a slot. Activity must match the member's package.</DialogDescription>
          </DialogHeader>
          <Tabs value={slotDayType} onValueChange={setSlotDayType}>
            <TabsList>
              <TabsTrigger value="weekday" data-testid="slot-tab-weekday">Weekdays</TabsTrigger>
              <TabsTrigger value="weekend" data-testid="slot-tab-weekend">Weekend</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="py-2 space-y-1.5 max-h-[55vh] overflow-auto">
            {allSlots.filter((b) => b.day_type === slotDayType).sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')).map((b) => {
              const enrolled = scheduleSlots.some((s) => s.id === b.id);
              return (
                <div key={b.id} className="flex items-center justify-between gap-2 p-2 border border-gray-100 rounded-lg" data-testid={`pick-slot-${b.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-800 w-24">{slotLbl(b)}</span>
                    <Badge className={ACT[b.activity_type]?.color}>{ACT[b.activity_type]?.label}</Badge>
                  </div>
                  {enrolled ? <Badge variant="secondary" className="text-xs">Enrolled</Badge> : (
                    <Button size="sm" variant="outline" onClick={() => enrollSlot(b.id, false)} data-testid={`pick-enroll-${b.id}`}>Add</Button>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Slot audience-mismatch warning */}
      <Dialog open={!!slotWarn} onOpenChange={(o) => !o && setSlotWarn(null)}>
        <DialogContent data-testid="slot-warn-dialog">
          <DialogHeader>
            <DialogTitle>Check this slot</DialogTitle>
            <DialogDescription>{slotWarn?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlotWarn(null)}>Cancel</Button>
            <Button onClick={() => enrollSlot(slotWarn.batchId, true)} className="bg-amber-500 hover:bg-amber-600" data-testid="slot-warn-confirm">Enroll Anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberDetailPage;
